import { useRef, useState } from "react";
import MonacoEditor, { loader, Monaco } from "@monaco-editor/react";
import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography as T,
} from "@mui/material";
import * as monaco from "~/utils/monaco-editor";

loader.config({ monaco });

interface MarkdownModalProps {
  error?: string;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (text: string) => void | Promise<void>;
  open: boolean;
  title: string;
  value: string;
}

const MarkdownModal = ({
  error,
  loading = false,
  onClose,
  onSubmit,
  open,
  title,
  value,
}: MarkdownModalProps) => {
  const monacoRef = useRef<Monaco | null>(null);

  const [text, setText] = useState(value);

  const handleSubmit = () => {
    onSubmit(text);
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle>
        <Stack>
          {title}
          <T variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Supports Markdown. Use <strong>**bold**</strong>, <em>*italic*</em>, lists, and more.
          </T>
        </Stack>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ position: "absolute", right: 16, top: 16 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert sx={{ mb: 2 }} severity="error">
            {error}
          </Alert>
        )}
        <Box sx={{ border: "1px solid #E0E0E7", borderRadius: "6px", overflow: "hidden" }}>
          <MonacoEditor
            height="50vh"
            language="markdown"
            value={text}
            beforeMount={(monaco) => {
              monacoRef.current = monaco;
            }}
            onChange={(v) => setText(v ?? "")}
            onMount={(editor) => editor.focus()}
            options={{
              automaticLayout: true,
              fixedOverflowWidgets: true,
              formatOnPaste: true,
              formatOnType: true,
              minimap: { enabled: false },
              readOnly: loading,
              scrollBeyondLastLine: false,
              wordBasedSuggestions: "off",
              wordWrap: "on",
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={loading}
          variant="contained"
          color="secondary"
          onClick={onClose}
          sx={{ bgcolor: "grey.100", minWidth: 70, px: 1.25, py: 1 }}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          onClick={handleSubmit}
          sx={{ minWidth: 70, px: 1.25, py: 1 }}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default MarkdownModal;
